<script lang="ts">
  // Review interface per INTERFACE-SPECIFICATION.md §8.2.
  //
  // Layout: cube cell deltas table, marketing-surface effect table,
  // validation results (six categories), Approve / Request Changes
  // buttons. Approve runs through POST /api/proposals/{n}/approve.
  // The proxy Worker re-runs validateChange and enforces both the
  // reviewer:{value_type} role and the no-self-approval gate.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { navigate } from '../lib/router.svelte';
  import { identity } from '../lib/identity.svelte';
  import { reviewerRoleFor } from '../lib/roles';
  import { findProduct } from '../lib/products';
  import { formatLocalDate, formatRate, formatRateDelta, formatAmount, formatAmountDelta, deltaCssClass, formatEffectiveTimestamp, formatClampedFromNote } from '../lib/formatters';
  import { projectMarketingBand, PUBLIC_BANDS } from '../lib/surface-projector';
  import type { ProposalDetail } from '../lib/types';
  import ProposalTimeline from './ProposalTimeline.svelte';
  import PurposeAmountRangeDelta from './PurposeAmountRangeDelta.svelte';
  import {
    diffAmountRange,
    diffPurposes,
    diffFees
  } from '../lib/proposal-preview-helpers';

  interface Props {
    prNumber: number;
  }

  const { prNumber }: Props = $props();

  let detail = $state<ProposalDetail | null>(null);
  let loading = $state<boolean>(true);
  let error = $state<string | null>(null);
  let actionInFlight = $state<'approve' | 'request' | null>(null);
  let actionError = $state<string | null>(null);
  let requestComment = $state<string>('');
  let requestOpen = $state<boolean>(false);

  onMount(() => {
    void load();
  });

  async function load() {
    loading = true;
    error = null;
    try {
      detail = await api.loadProposal(prNumber);
    } catch (err) {
      error = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'load_proposal_failed';
    } finally {
      loading = false;
    }
  }

  async function approve() {
    actionInFlight = 'approve';
    actionError = null;
    try {
      const result = await api.approveProposal(prNumber);
      if (result.merged) {
        navigate(`/changes/recently-completed/${prNumber}`);
      } else {
        await load();
      }
    } catch (err) {
      actionError = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'approve_failed';
    } finally {
      actionInFlight = null;
    }
  }

  async function requestChanges() {
    if (!requestComment.trim()) {
      actionError = 'A comment is required when requesting changes.';
      return;
    }
    actionInFlight = 'request';
    actionError = null;
    try {
      await api.requestChanges(prNumber, requestComment.trim());
      requestOpen = false;
      requestComment = '';
      await load();
    } catch (err) {
      actionError = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'request_changes_failed';
    } finally {
      actionInFlight = null;
    }
  }

  function openRevise() {
    // Navigate to the full cube editor in revise mode (RevisionEditor).
    // The proposer needs the same affordances they had when creating
    // the proposal — add, remove, or modify ANY cell, not only the
    // ones already in the proposal — so the inline form on this view
    // would be too restrictive. The RevisionEditor route pre-seeds
    // the cube editor with the proposal's existing deltas and submits
    // to /api/proposals/{n}/revise.
    navigate(`/changes/pending-review/${prNumber}/revise`);
  }

  const reviewerRole = $derived(detail ? reviewerRoleFor(detail.value_type) : '');
  const canApprove = $derived(
    detail
      ? identity.roles.includes('admin') || identity.roles.includes(reviewerRole)
      : false
  );
  const isSelf = $derived(detail?.proposed_by?.toLowerCase() === identity.email?.toLowerCase());
  const marketingEffect = $derived(buildMarketingEffect());

  // Value-type-specific previews. For 'rates' proposals the existing
  // cube + marketing tables are the right register. For 'amounts',
  // 'purposes', and 'fees' proposals detail.deltas is empty (the
  // server's diffCubes does not surface non-rate changes), so we
  // diff detail.current vs detail.candidate client-side and render
  // a value-type-shaped summary.
  const amountDiff = $derived(detail ? diffAmountRange(detail.current, detail.candidate) : []);
  const purposeDiff = $derived(detail ? diffPurposes(detail.current, detail.candidate) : []);
  const feeDiff = $derived(detail ? diffFees(detail.current, detail.candidate) : []);

  function buildMarketingEffect() {
    if (!detail) return [];
    return PUBLIC_BANDS.map((band) => {
      const before = projectMarketingBand(detail!.current, band);
      const after = projectMarketingBand(detail!.candidate, band);
      const delta = before.min_rate !== null && after.min_rate !== null ? Number(after.min_rate) - Number(before.min_rate) : null;
      return { band, before, after, delta };
    });
  }

  function bandLabel(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
  function productLabel() {
    if (!detail || !detail.jurisdiction || !detail.product) return '—';
    const desc = findProduct(detail.jurisdiction, detail.product);
    return desc ? desc.label + ' · ' + desc.customerLabel : detail.jurisdiction + '/' + detail.product;
  }
</script>

<section>
  {#if loading}
    <p class="muted">Loading review…</p>
  {:else if error}
    <div class="banner banner-fail">{error}</div>
  {:else if !detail}
    <p class="muted">Proposal not found.</p>
  {:else}
    <header class="review-header">
      <h2>Review · #{detail.pr_number} · {productLabel()}</h2>
      <p class="muted">{detail.change_description}</p>
    </header>

    <dl class="review-meta">
      <div><dt>Proposed by</dt><dd>{detail.proposed_by}</dd></div>
      <div><dt>Reviewer</dt><dd>{detail.reviewer ?? '—'}</dd></div>
      <div>
        <dt>Effective from</dt>
        <dd>
          {formatEffectiveTimestamp(detail.effective_from)}
          {#if detail.proposed_effective_from}
            <div class="clamp-note muted">{formatClampedFromNote(detail.proposed_effective_from)}</div>
          {/if}
        </dd>
      </div>
      <div>
        <dt>Substantiation</dt>
        <dd>
          {#if detail.substantiation_url}
            <a href={detail.substantiation_url} target="_blank" rel="noopener" data-external>Open in Confluence</a>
          {:else}
            —
          {/if}
        </dd>
      </div>
    </dl>

    {#if detail.value_type === 'rates'}
    <h3 class="section-heading">Cube cell changes</h3>
    <!-- Two renderings of the same data drive off the viewport:
         the dense table on desktop (default register), the stacked
         compact card list on mobile per INTERFACE-SPECIFICATION.md
         §12.7 — the load-bearing mobile case is the reviewer who
         opens a proposal notification from a phone and needs to see
         exactly which cells are changing without horizontal scroll. -->
    <div class="review-deltas review-deltas--table">
      <table class="cube-table">
        <thead>
          <tr>
            <th>Coordinates</th>
            <th class="num">% before</th>
            <th class="num">% proposed</th>
            <th class="num">Δ %</th>
            <th class="num">$ before</th>
            <th class="num">$ proposed</th>
            <th class="num">Δ $</th>
          </tr>
        </thead>
        <tbody>
          <!--
            The server returns deltas in the diffCubes shape:
                { coordinates, previous: CubeCellValue, next: CubeCellValue }
            Each row has both rate and amount sub-fields; the SPA's flat
            CellDelta type (field/before/after/delta) is aspirational and
            does not match what loadProposal actually emits. Read previous /
            next directly, compute rate and amount deltas inline, and
            render "—" only when a specific field is unchanged between
            previous and next.
          -->
          {#each detail.deltas as d (d.coordinates)}
            {@const prev = (d as unknown as { previous?: { interest_rate_pa?: number | null; max_loan_amount?: number | null } }).previous}
            {@const next = (d as unknown as { next?: { interest_rate_pa?: number | null; max_loan_amount?: number | null } }).next}
            {@const beforeRate = prev?.interest_rate_pa ?? null}
            {@const afterRate = next?.interest_rate_pa ?? null}
            {@const beforeAmount = prev?.max_loan_amount ?? null}
            {@const afterAmount = next?.max_loan_amount ?? null}
            {@const rateChanged = beforeRate !== afterRate && beforeRate !== null && afterRate !== null}
            {@const amountChanged = beforeAmount !== afterAmount && beforeAmount !== null && afterAmount !== null}
            {@const rateDelta = rateChanged ? Number(afterRate) - Number(beforeRate) : null}
            {@const amountDelta = amountChanged ? Number(afterAmount) - Number(beforeAmount) : null}
            <tr>
              <td><code>{d.coordinates}</code></td>
              <td class="num">{rateChanged ? formatRate(beforeRate) : '—'}</td>
              <td class="num">{rateChanged ? formatRate(afterRate) : '—'}</td>
              <td class="num {deltaCssClass(rateDelta)}">{rateChanged ? formatRateDelta(rateDelta) : '—'}</td>
              <td class="num">{amountChanged ? formatAmount(beforeAmount) : '—'}</td>
              <td class="num">{amountChanged ? formatAmount(afterAmount) : '—'}</td>
              <td class="num {deltaCssClass(amountDelta)}">{amountChanged ? formatAmountDelta(amountDelta) : '—'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <ul class="review-deltas review-deltas--cards">
      {#each detail.deltas as d (d.coordinates)}
        {@const prev = (d as unknown as { previous?: { interest_rate_pa?: number | null; max_loan_amount?: number | null } }).previous}
        {@const next = (d as unknown as { next?: { interest_rate_pa?: number | null; max_loan_amount?: number | null } }).next}
        {@const beforeRate = prev?.interest_rate_pa ?? null}
        {@const afterRate = next?.interest_rate_pa ?? null}
        {@const beforeAmount = prev?.max_loan_amount ?? null}
        {@const afterAmount = next?.max_loan_amount ?? null}
        {@const rateChanged = beforeRate !== afterRate && beforeRate !== null && afterRate !== null}
        {@const amountChanged = beforeAmount !== afterAmount && beforeAmount !== null && afterAmount !== null}
        {@const rateDelta = rateChanged ? Number(afterRate) - Number(beforeRate) : null}
        {@const amountDelta = amountChanged ? Number(afterAmount) - Number(beforeAmount) : null}
        <li class="review-delta-card">
          <code class="review-delta-card__coord">{d.coordinates}</code>
          {#if rateChanged}
            <div class="review-delta-card__row">
              <span class="muted">Rate</span>
              <span class="review-delta-card__values">
                {formatRate(beforeRate)} → {formatRate(afterRate)}
                <span class="review-delta-card__delta {deltaCssClass(rateDelta)}">{formatRateDelta(rateDelta)}</span>
              </span>
            </div>
          {/if}
          {#if amountChanged}
            <div class="review-delta-card__row">
              <span class="muted">Max amount</span>
              <span class="review-delta-card__values">
                {formatAmount(beforeAmount)} → {formatAmount(afterAmount)}
                <span class="review-delta-card__delta {deltaCssClass(amountDelta)}">{formatAmountDelta(amountDelta)}</span>
              </span>
            </div>
          {/if}
        </li>
      {/each}
    </ul>

    <h3 class="section-heading">Marketing-surface effect</h3>
    <div class="table-scroll">
      <table class="cube-table">
        <thead>
          <tr>
            <th>Public band</th>
            <th class="num">% In Effect (min)</th>
            <th class="num">% Proposed (min)</th>
            <th class="num">Δ %</th>
          </tr>
        </thead>
        <tbody>
          {#each marketingEffect as row (row.band)}
            <tr>
              <td>{bandLabel(row.band)}</td>
              <td class="num">{formatRate(row.before.min_rate)}</td>
              <td class="num">{formatRate(row.after.min_rate)}</td>
              <td class="num {deltaCssClass(row.delta)}">{formatRateDelta(row.delta)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    {:else if detail.value_type === 'amounts'}
      <h3 class="section-heading">Loan amount range changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Field</th>
              <th class="num">$ before</th>
              <th class="num">$ proposed</th>
              <th class="num">Δ $</th>
            </tr>
          </thead>
          <tbody>
            {#each amountDiff.filter((r) => r.changed) as row (row.field)}
              <tr>
                <td><strong>{row.field === 'min' ? 'Minimum' : row.field === 'max' ? 'Maximum' : 'Increment'}</strong></td>
                <td class="num">{row.before !== undefined ? '$' + formatAmount(row.before) : '—'}</td>
                <td class="num">{row.after !== undefined ? '$' + formatAmount(row.after) : '—'}</td>
                <td class="num {deltaCssClass(row.delta)}">{row.delta !== null ? (row.delta > 0 ? '+' : '') + formatAmount(row.delta) : '—'}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else if detail.value_type === 'purposes'}
      <h3 class="section-heading">Loan purpose changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>ID</th>
              <th>Display name</th>
              <th>Amount range</th>
            </tr>
          </thead>
          <tbody>
            {#each purposeDiff as row (row.purpose_id)}
              <tr>
                <td><strong>{row.action.charAt(0).toUpperCase() + row.action.slice(1)}</strong></td>
                <td><code>{row.purpose_id}</code></td>
                <td>
                  {#if row.action === 'remove'}
                    <span class="muted">{row.before?.display_name ?? '—'}</span>
                  {:else if row.action === 'update'}
                    {row.before?.display_name !== row.after?.display_name
                      ? (row.before?.display_name ?? '—') + ' → ' + (row.after?.display_name ?? '—')
                      : (row.after?.display_name ?? '—')}
                  {:else}
                    {row.after?.display_name ?? '—'}
                  {/if}
                </td>
                <td>
                  {#if row.action === 'remove'}
                    <span class="muted">
                      <PurposeAmountRangeDelta before={row.before?.amount_range ?? null} after={null} />
                    </span>
                  {:else if row.action === 'update'}
                    <PurposeAmountRangeDelta before={row.before?.amount_range ?? null} after={row.after?.amount_range ?? null} />
                  {:else}
                    <PurposeAmountRangeDelta before={null} after={row.after?.amount_range ?? null} />
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else if detail.value_type === 'fees'}
      <h3 class="section-heading">Fee schedule changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Path</th>
              <th>Before</th>
              <th>Proposed</th>
            </tr>
          </thead>
          <tbody>
            {#each feeDiff as row (row.path)}
              <tr>
                <td><code>{row.path}</code></td>
                <td><code class="diff-payload">{JSON.stringify(row.before)}</code></td>
                <td><code class="diff-payload">{JSON.stringify(row.after)}</code></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else}
      <h3 class="section-heading">Changes</h3>
      <p class="muted">No structured preview available for value type "{detail.value_type}".</p>
    {/if}

    <h3 class="section-heading">Proposal history</h3>
    <ProposalTimeline {prNumber} />

    {#if detail.validation_result}
      <h3 class="section-heading">Validation results</h3>
      <ul class="validation-list">
        <li class="status-pass">✓ Schema: {detail.validation_result.schemaErrors.length === 0 ? 'passed' : detail.validation_result.schemaErrors.length + ' error(s)'}</li>
        <li class:status-pass={detail.validation_result.symmetryFailures.length === 0} class:status-fail={detail.validation_result.symmetryFailures.length > 0}>
          {detail.validation_result.symmetryFailures.length === 0 ? '✓' : '✗'} Cross-jurisdiction / within-cube symmetry: {detail.validation_result.symmetryFailures.length} undeclared failure(s)
        </li>
        {#if detail.validation_result.symmetryDeclared.length > 0}
          <li class="status-warn">⚠ Declared asymmetries acknowledged: {detail.validation_result.symmetryDeclared.length}</li>
        {/if}
      </ul>
      {#if detail.validation_result.symmetryDeclared.length > 0}
        <!-- Enumerate the declared asymmetries so the reviewer sees the
             specific rules the proposer acknowledged, not just a count.
             Approving the proposal accepts each of these. Unkeyed each
             — the list is small and rebuilds on every loadProposal. -->
        <ul class="declared-asymmetries">
          {#each detail.validation_result.symmetryDeclared as entry}
            <li>
              <strong>{entry.category}</strong>
              <span class="muted"> — {entry.label}</span>
            </li>
          {/each}
        </ul>
      {/if}
    {/if}

    {#if actionError}
      <div class="banner banner-fail">{actionError}</div>
    {/if}

    <footer class="review-footer">
      {#if detail.status === 'merged'}
        <p class="muted">
          This proposal was merged. The cube change is live (or
          scheduled for its effective-from instant); no further action
          is possible from this view.
        </p>
      {:else if detail.status === 'closed'}
        <p class="muted">
          This proposal was closed without merging. Approve, Request
          changes, and Revise are all unavailable on a closed
          proposal. If iteration is needed, start a fresh draft from
          the Catalogue view — it will open a new proposal PR with its
          own review thread.
        </p>
      {:else if isSelf}
        <p class="muted">You proposed this change; another reviewer must approve.</p>
        <button class="btn" type="button" onclick={openRevise} disabled={actionInFlight !== null}>
          Revise this proposal
        </button>
      {:else if !canApprove}
        <p class="muted">Your role does not include {reviewerRole}. Approval and Request Changes are hidden.</p>
      {:else}
        <button class="btn review-footer__request" type="button" onclick={() => (requestOpen = !requestOpen)} disabled={actionInFlight !== null}>
          {requestOpen ? 'Cancel request changes' : 'Request changes'}
        </button>
        <button
          class="btn-approve btn-approve-fullwidth"
          type="button"
          onclick={approve}
          disabled={actionInFlight !== null}
        >
          {actionInFlight === 'approve' ? 'Approving…' : 'Approve →'}
        </button>
      {/if}
    </footer>

    {#if requestOpen && canApprove && !isSelf && detail.status !== 'merged' && detail.status !== 'closed'}
      <div class="request-form">
        <label>
          <span>Comment</span>
          <textarea rows="3" bind:value={requestComment} placeholder="What needs to change before this can be approved?"></textarea>
        </label>
        <button class="btn" type="button" onclick={requestChanges} disabled={actionInFlight !== null}>
          {actionInFlight === 'request' ? 'Submitting…' : 'Submit request →'}
        </button>
      </div>
    {/if}

  {/if}
</section>

<style>
  .review-header h2 { margin-bottom: var(--space-1); }
  .review-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--space-3);
    margin: var(--space-4) 0;
  }
  .review-meta dt { font-size: var(--type-sm); color: var(--surface-muted); }
  .review-meta dd { margin: 0; font-size: var(--type-sm); }
  .validation-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-1); font-family: var(--font-mono); font-size: var(--type-sm); }
  .declared-asymmetries {
    list-style: disc;
    padding-left: var(--space-4);
    margin: var(--space-1) 0 var(--space-3) 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--type-sm);
  }
  .review-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    margin-top: var(--space-5);
    padding-top: var(--space-3);
    border-top: 1px solid var(--surface-line-soft);
    align-items: center;
  }
  .request-form {
    margin-top: var(--space-3);
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .request-form textarea {
    width: 100%;
    padding: var(--space-2);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
  }
  /* Review history thread — chronological, oldest first, with the
   * latest active state visually distinguished (approved is muted-pass,
   * changes-requested is warn). Dismissed reviews are shown for
   * audit but muted further (the entry still appears in the trail). */
  code { font-family: var(--font-mono); }
  /* Fee-changes cells render the path's whole value as JSON so the
     reviewer sees exactly what is being mutated. Wrap aggressively
     so the establishment-fee tiers array does not blow out the
     table. Mirrors the same class in SubmitForReview.svelte. */
  code.diff-payload {
    display: inline-block;
    max-width: 24rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: var(--type-xs);
  }

  /* Per §12.7 the Review-and-Approve flow is the load-bearing
   * mobile case. The cube delta table is wider than 375 px would
   * carry; below 768 px we hide the table and show a stacked
   * per-cell card list instead. Above 768 px the table is the
   * primary register. The marketing-surface table is narrower
   * but still benefits from a scroll wrap so the columns never
   * truncate. */
  .table-scroll { overflow-x: auto; }
  .review-deltas--cards { display: none; }
  .review-delta-card {
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .review-delta-card__coord { font-size: var(--type-sm); margin-bottom: var(--space-1); }
  .review-delta-card__row {
    display: flex;
    justify-content: space-between;
    gap: var(--space-2);
    font-size: var(--type-sm);
  }
  .review-delta-card__values { font-variant-numeric: tabular-nums; }
  .review-delta-card__delta { margin-left: var(--space-2); font-weight: 600; }

  @media (max-width: 768px) {
    .review-deltas--table { display: none; }
    .review-deltas--cards {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
      list-style: none;
      padding: 0;
      margin: 0;
    }
    /* Stack the footer so Request changes sits above and Approve
     * spans the full width below — the spec calls for a visually
     * unmistakable Approve, separated by whitespace from the
     * secondary action. */
    .review-footer {
      flex-direction: column;
      align-items: stretch;
      gap: var(--space-3);
    }
    .review-footer__request {
      width: 100%;
    }
    .review-meta {
      grid-template-columns: 1fr;
    }
  }
</style>
